import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ReferralEvents, trackReferralEvent } from "@/utils/analytics";
import { getUnauthenticatedEntityData } from "@/utils/localStorageEntityData";

export type AuthMode = "signin" | "signup" | "forgot-password";

type AuthFormProps = {
  className?: string;
  mode?: AuthMode;
  onModeChange?: (mode: AuthMode) => void;
};

function GoogleIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z"
        fill="#EA4335"
      />
    </svg>
  );
}

function LinkedInIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15.335 0H2.665A2.665 2.665 0 0 0 0 2.665v12.67A2.665 2.665 0 0 0 2.665 18h12.67A2.665 2.665 0 0 0 18 15.335V2.665A2.665 2.665 0 0 0 15.335 0ZM5.339 15.337H2.67V6.747h2.67v8.59ZM4.004 5.682a1.548 1.548 0 1 1 0-3.096 1.548 1.548 0 0 1 0 3.096Zm11.335 9.655h-2.669V11.16c0-.996-.018-2.277-1.388-2.277-1.39 0-1.601 1.086-1.601 2.207v4.247H7.012V6.747h2.562v1.174h.036c.357-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.779 3.203 4.092v4.711Z"
        fill="#0A66C2"
      />
    </svg>
  );
}

export default function CustomAuthForm({
  className,
  mode: controlledMode,
  onModeChange,
}: AuthFormProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const [internalMode, setInternalMode] = useState<AuthMode>("signup");
  const mode = controlledMode ?? internalMode;
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [showReferral, setShowReferral] = useState(false);

  // Form data
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Stable unique IDs for a11y labels/inputs
  const baseId = useId();
  const emailId = `${baseId}-email`;
  const passwordId = `${baseId}-password`;
  const confirmPasswordId = `${baseId}-confirmPassword`;
  const referralCodeId = `${baseId}-referralCode`;

  useEffect(() => {
    // Check if there's a referral code in the URL or localStorage
    const urlParams = new URLSearchParams(location.search);
    const refCode =
      urlParams.get("ref") || localStorage.getItem("pendingReferralCode");

    if (refCode) {
      setReferralCode(refCode);
      localStorage.setItem("pendingReferralCode", refCode);
    }
  }, [location.search]);

  const signInWithGoogle = async () => {
    setLoading(true);
    try {
      await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
    } catch (error) {
      console.error("Error signing in with Google:", error);
      toast({
        title: "Eroare autentificare",
        description: "Nu s-a putut autentifica cu Google",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const signInWithLinkedin = async () => {
    setLoading(true);
    try {
      await supabase.auth.signInWithOAuth({
        provider: "linkedin_oidc",
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
    } catch (error) {
      console.error("Error signing in with LinkedIn:", error);
      toast({
        title: "Eroare autentificare",
        description: "Nu s-a putut autentifica cu LinkedIn",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      toast({
        title: "Succes",
        description: "Te-ai autentificat cu succes!",
      });

      const guestData = getUnauthenticatedEntityData();
      const isFromLandingPage = guestData?.currentStep === 0;

      let returnUrl: string;
      if (!guestData) {
        returnUrl = "/dashboard";
      } else if (isFromLandingPage) {
        returnUrl = "/companiile-mele";
      } else {
        returnUrl = "/identificare-raportare";
      }
      navigate(returnUrl);
    } catch (error: unknown) {
      console.error("Error signing in:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Nu s-a putut completa autentificarea";
      toast({
        title: "Eroare autentificare",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast({
        title: "Eroare",
        description: "Parolele nu se potrivesc",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/ghid-nis2`,
        },
      });

      if (error) throw error;

      // If there's a referral code, track it for later validation
      if (referralCode) {
        trackReferralEvent({
          event: ReferralEvents.CODE_ENTERED,
          referralCode: referralCode,
          metadata: { userEmail: email },
        });

        // Store the referral code to be validated after email confirmation
        localStorage.setItem(
          "pendingReferralValidation",
          JSON.stringify({
            referralCode,
            userEmail: email,
            timestamp: new Date().toISOString(),
          })
        );
      }

      toast({
        title: "Cont creat cu succes",
        description: "Verifică-ți emailul pentru confirmarea contului",
      });

      const guestData = getUnauthenticatedEntityData();

      const isFromLandingPage = guestData?.currentStep === 0;

      let returnUrl: string;
      if (!guestData) {
        returnUrl = "/ghid-nis2";
      } else if (isFromLandingPage) {
        returnUrl = "/companiile-mele";
      } else {
        returnUrl = "/identificare-raportare";
      }
      switchMode("signin");
      navigate(returnUrl);
    } catch (error: unknown) {
      console.error("Error signing up:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Nu s-a putut crea contul";
      toast({
        title: "Eroare înregistrare",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      toast({
        title: "Email trimis",
        description: "Verifică-ți emailul pentru instrucțiunile de resetare",
      });

      switchMode("signin");
    } catch (error: unknown) {
      console.error("Error resetting password:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Nu s-a putut trimite emailul de resetare";
      toast({
        title: "Eroare",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEmail("");
    setPassword("");
    setConfirmPassword("");
  };

  const switchMode = (newMode: AuthMode) => {
    if (onModeChange) {
      onModeChange(newMode);
    } else {
      setInternalMode(newMode);
    }
    resetForm();
  };

  return (
    <div className={className}>
      <div className="space-y-6">
        <form
          onSubmit={
            mode === "signin"
              ? handleSignIn
              : mode === "signup"
                ? handleSignUp
                : handleForgotPassword
          }
          className="space-y-3"
        >
          {/* ===== SIGNUP / SIGNIN: OAuth providers at the top ===== */}
          {mode !== "forgot-password" && (
            <>
              <div className="text-center mb-4">
                <p className="text-sm text-muted-foreground">
                  {mode === "signup"
                    ? "Creează rapid cont prin Google sau LinkedIn"
                    : "Autentifică-te rapid prin Google sau LinkedIn"}
                </p>
              </div>
              <div className="flex gap-3 mb-2">
                <button
                  type="button"
                  className="auth-oauth-btn w-1/2 flex items-center justify-center gap-2"
                  onClick={signInWithGoogle}
                >
                  <GoogleIcon /> Google
                </button>
                <button
                  type="button"
                  className="auth-oauth-btn w-1/2 flex items-center justify-center gap-2"
                  onClick={signInWithLinkedin}
                >
                  <LinkedInIcon /> LinkedIn
                </button>
              </div>
              <div className="auth-divider">sau prin email</div>
            </>
          )}

          {/* Referral Code Display (when pre-filled from URL) */}
          {referralCode && mode === "signup" && !showReferral && (
            <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium">Cod de recomandare aplicat:</span>
                <code className="bg-background px-2 py-1 rounded text-primary font-mono">
                  {referralCode}
                </code>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Vei primi credite bonus după confirmarea contului!
              </p>
            </div>
          )}

          {/* Email field */}
          <div className="space-y-2.5">
            <Label
              htmlFor={emailId}
              className="text-sm font-medium text-muted-foreground"
            >
              Introduceți adresa de email
            </Label>
            <Input
              id={emailId}
              type="email"
              placeholder="nume@companie.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="h-12 text-base"
              autoComplete="username"
            />
          </div>

          {/* Password field - not shown for forgot password */}
          {mode !== "forgot-password" && (
            <div className="space-y-2.5">
              <Label
                htmlFor={passwordId}
                className="text-sm font-medium text-muted-foreground"
              >
                Introduceți parola
              </Label>
              <div className="relative">
                <Input
                  id={passwordId}
                  type={showPassword ? "text" : "password"}
                  placeholder="Introduceți parola"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 text-base pr-10"
                  autoComplete="current-password"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* "Ai uitat parola?" right after password in signin mode */}
          {mode === "signin" && (
            <div className="text-right">
              <button
                type="button"
                onClick={() => switchMode("forgot-password")}
                className="text-sm text-primary hover:underline"
              >
                Ai uitat parola?
              </button>
            </div>
          )}

          {/* Confirm password for signup */}
          {mode === "signup" && (
            <div className="space-y-2.5">
              <Label
                htmlFor={confirmPasswordId}
                className="text-sm font-medium text-muted-foreground"
              >
                Confirmă parola
              </Label>
              <Input
                id={confirmPasswordId}
                type="password"
                placeholder="Confirmă parola"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className={`h-12 text-base ${
                  confirmPassword && password !== confirmPassword
                    ? "border-red-500 focus:border-red-500"
                    : ""
                }`}
              />
              {confirmPassword && password !== confirmPassword && (
                <p className="text-sm text-red-500">Parolele nu se potrivesc</p>
              )}
            </div>
          )}

          {/* Referral code collapsible for signup */}
          {mode === "signup" && !referralCode && (
            <div className="space-y-2">
              {!showReferral ? (
                <button
                  type="button"
                  onClick={() => setShowReferral(true)}
                  className="text-sm text-primary hover:underline"
                >
                  Am un cod de recomandare
                </button>
              ) : (
                <div className="space-y-2.5">
                  <Label
                    htmlFor={referralCodeId}
                    className="text-sm font-medium text-muted-foreground"
                  >
                    Cod de recomandare
                  </Label>
                  <Input
                    id={referralCodeId}
                    placeholder="Introdu codul de recomandare"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value)}
                    className="h-12 text-base"
                  />
                  <p className="text-xs text-muted-foreground">
                    Dacă ai fost invitat de un coleg, introdu codul pentru
                    credite bonus
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Submit button */}
          <Button
            type="submit"
            className="w-full h-12 text-base font-medium tracking-tight"
            disabled={
              loading ||
              (mode === "signup" &&
                confirmPassword.length > 0 &&
                password !== confirmPassword)
            }
          >
            {loading ? (
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            ) : mode === "signin" ? (
              "Continuă cu email"
            ) : mode === "signup" ? (
              "Creează cont"
            ) : (
              "Trimite instrucțiuni"
            )}
          </Button>

          {/* "Ai deja un cont?" right after submit in signup mode */}
          {mode === "signup" && (
            <p className="text-sm text-muted-foreground text-center">
              Ai deja un cont?{" "}
              <button
                type="button"
                onClick={() => switchMode("signin")}
                className="text-primary hover:underline font-medium"
              >
                Autentifică-te
              </button>
            </p>
          )}

          {/* Terms and conditions text for signup */}
          {mode === "signup" && (
            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                Prin continuare ești de acord cu{" "}
                <a
                  href="/termeni-si-conditii"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary/80 transition-colors underline"
                >
                  Termenii si condițiile
                </a>{" "}
                și{" "}
                <a
                  href="/politica-de-confidentialitate"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary/80 transition-colors underline"
                >
                  Politica de prelucrare a datelor
                </a>
              </p>
            </div>
          )}
        </form>

        {/* Mode switching links */}
        <div className="space-y-2">
          {mode === "signin" && (
            <p className="text-sm text-muted-foreground">
              Nu aveți un cont?{" "}
              <button
                type="button"
                onClick={() => switchMode("signup")}
                className="text-primary hover:underline font-medium"
              >
                Înregistrați-vă
              </button>
            </p>
          )}

          {mode === "forgot-password" && (
            <p className="text-sm text-muted-foreground">
              Înapoi la{" "}
              <button
                type="button"
                onClick={() => switchMode("signin")}
                className="text-primary hover:underline font-medium"
              >
                autentificare
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
